import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import api from "@/lib/api";
import { relDate } from "@/lib/crm";
import { cn } from "@/lib/utils";

export default function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const openNotif = async (n) => {
    await api.post(`/notifications/${n.id}/read`).catch(() => {});
    setOpen(false);
    load();
    if (n.lead_id) navigate(`/leads/${n.lead_id}`);
  };

  const markAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notif-bell" onClick={() => { setOpen((o) => !o); if (!open) load(); }}
        className="relative p-2 rounded-md hover:bg-accent transition-colors">
        <Bell className="w-4 h-4" />
        {data.unread > 0 && (
          <span data-testid="notif-count" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-80 bg-popover border rounded-md shadow-lg z-50 overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b">
            <span className="font-heading font-bold text-sm">Notifiche</span>
            {data.unread > 0 && <button onClick={markAll} className="text-xs text-muted-foreground hover:text-foreground">Segna tutte lette</button>}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {data.items.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">Nessuna notifica</div>
            ) : data.items.map((n) => (
              <button key={n.id} data-testid={`notif-${n.id}`} onClick={() => openNotif(n)}
                className={cn("w-full text-left px-3 py-2.5 hover:bg-accent transition-colors border-b last:border-0 flex gap-2", !n.read && "bg-blue-500/5")}>
                {!n.read && <span className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />}
                <div className={cn("min-w-0", n.read && "pl-4")}>
                  <div className="text-sm truncate">{n.text}</div>
                  <div className="text-xs text-muted-foreground">{relDate(n.created_at)}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
